import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import {
  DragIndicator,
  HighlightOff,
  LocationOn,
  MoreHoriz,
} from "@mui/icons-material"
import {
  Avatar,
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material"
import { useMap } from "@vis.gl/react-google-maps"
import { createRef, FC, memo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Address } from "src/db/Address"
import { OrderViewModel } from "src/db/Order"
import {
  DeliveryConstraintEnum,
  OrderLayoutOptionsEnum,
  OrderLayoutOptionsObj,
} from "src/features/config-setting/constants"
import { OrderCardLayoutConfig } from "src/features/config-setting/models"
import {
  OrderDetailViewContainer,
  OrderTagLabel,
} from "src/features/order/components"
import { OrderDetailViewType } from "src/features/order/constants"
import {
  MenuWrapper,
  SquareIcon,
  TooltipWrapper,
} from "src/features/shared-module/components"
import { appConstants } from "src/features/shared-module/constants"
import { getFullName } from "src/features/shared-module/functions"
import { useOrderContex } from "../context"
import { useAppDispatch, useAppSelector } from "../hooks"
import { useEmployeeColor } from "../hooks/useRouteDetail"
import {
  haulerDetailMapSelector,
  setOrderAnimateId,
  setProviderAnimateId,
} from "../slices/dashboard.slice"
import { OrderDeliveryProgress } from "./OrderDeliveryProgress"
import { OrderStatusIcon } from "./OrderStatusIcon"

export const OrderCard: FC<{
  order: OrderViewModel
  showDivider?: boolean
  listeners?: any
  orderCardLayoutConfig?: OrderCardLayoutConfig
  isSelected?: boolean
  onToggleSelect?: (orderId: number, shiftKey: boolean) => void
  selectable?: boolean
}> = memo(
  ({
    order,
    listeners,
    orderCardLayoutConfig,
    showDivider = false,
    isSelected = false,
    onToggleSelect,
    selectable = false,
  }) => {
    const { t } = useTranslation()

    const { orderDetailRefs } = useOrderContex()
    if (!orderDetailRefs?.[order.id]) {
      orderDetailRefs[order.id] = createRef()
    }

    const dispatch = useAppDispatch()
    const haulerDetailMap = useAppSelector(haulerDetailMapSelector)
    const isAnimating = useAppSelector(
      (state) => state.dashboard.orderAnimateId === order.id,
    )
    const orderCardConfig = useAppSelector((state) => {
      if (state.dashboard.orderCardLayoutConfig) {
        return state.dashboard.orderCardLayoutConfig
      } else if (orderCardLayoutConfig) {
        return orderCardLayoutConfig
      }
      return null
    })

    const haulerEmployeeColor = useEmployeeColor(order.haulerEmployeeId)

    const map = useMap()

    const [orderDetailType, setOrderDetailType] =
      useState<OrderDetailViewType>(null)

    const setAnimateId = (module: "order" | "provider", id: number | null) => {
      if (orderCardLayoutConfig) {
        return
      }
      if (module === "order") {
        dispatch(setOrderAnimateId(id))
      } else {
        dispatch(setProviderAnimateId(id))
      }
    }

    const panToLocation = (address: Address) => {
      if (!map) return
      map.panTo({
        lat: address.position.latitude,
        lng: address.position.longitude,
      })
      map.setZoom(12)
    }

    const openOrCloseOrderDetail = (detailType: OrderDetailViewType) => {
      setOrderDetailType(detailType)
    }

    return (
      <Box
        {...uiSurface("UI-DSP-021")}
        data-testid={`order-card-${order.id}`}
        sx={{
          display: "flex",
          height: appConstants.orderRawHeight,
          position: "relative",
        }}
        ref={orderDetailRefs[order.id]}
      >
        <Box
          sx={(theme) => ({
            position: "absolute",
            right: 4,
            top: 2,
            backgroundColor: theme.palette.background.paper,
            zIndex: 1,
          })}
        >
          <MenuWrapper
            menuIcon={<MoreHoriz color="action" fontSize="small" />}
            menuProps={{ slotProps: { paper: uiPaper("UI-DSP-022") } }}
          >
            <MenuItem
              data-testid={`order-menu-detail-${order.id}`}
              onClick={() => openOrCloseOrderDetail("order")}
            >
              {t("viewOrderDetail")}
            </MenuItem>
            <MenuItem
              data-testid={`order-menu-manifest-${order.id}`}
              onClick={() => openOrCloseOrderDetail("manifest")}
            >
              {t("viewManifest")}
            </MenuItem>
            <MenuItem
              data-testid={`order-menu-tracking-${order.id}`}
              onClick={() => openOrCloseOrderDetail("tracking")}
            >
              {t("trackOrder")}
            </MenuItem>
          </MenuWrapper>
        </Box>
        {showDivider && (
          <Box
            sx={{
              width: 50,
              position: "relative",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Divider
              orientation="vertical"
              sx={{
                height: "calc(100% + 8px)",
                "&:before, &:after": {
                  borderLeftWidth: 3,
                  borderColor: haulerEmployeeColor,
                },
              }}
            >
              <Avatar
                sx={(theme) => ({
                  border: "4px solid",
                  borderColor: haulerEmployeeColor,
                  height: 35,
                  width: 35,
                  font: theme.typography.subtitle1,
                  backgroundColor: theme.palette.common.white,
                  color: theme.palette.primary.main,
                })}
              >
                {order.sequence ?? 0}
              </Avatar>
            </Divider>
          </Box>
        )}
        <Paper
          variant={"outlined"}
          onMouseEnter={() => setAnimateId("order", order.id)}
          onMouseLeave={() => setAnimateId("order", null)}
          onClick={(e) => {
            if (selectable && onToggleSelect) {
              onToggleSelect(order.id, e.shiftKey)
            }
          }}
          sx={(theme) => ({
            display: "flex",
            borderRadius: 2,
            padding: 1.5,
            borderWidth: isSelected ? 2 : 1,
            height: "100%",
            width: showDivider ? "calc(100% - 50px)" : "100%",
            overflow: "hidden",
            borderColor: isSelected
              ? theme.palette.primary.main
              : theme.palette.divider,
            backgroundColor: isSelected
              ? `${theme.palette.primary.main}08`
              : undefined,
            cursor: selectable ? "pointer" : undefined,
            transition:
              "box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
            "&:hover": {
              borderColor: isSelected
                ? theme.palette.primary.dark
                : theme.palette.primary.light,
              boxShadow: `0 2px 8px ${theme.palette.action.hover}`,
            },
          })}
        >
          <Stack direction={"row"} alignItems={"center"}>
            {selectable && (
              <Checkbox
                checked={isSelected}
                size="small"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation()
                  if (onToggleSelect) {
                    onToggleSelect(order.id, e.shiftKey)
                  }
                }}
                sx={{ p: 0.5, mr: 0.5 }}
              />
            )}
            {listeners && isAnimating ? (
              <IconButton
                {...listeners}
                sx={{
                  cursor: "move",
                }}
              >
                <DragIndicator color="secondary" />
              </IconButton>
            ) : (
              <IconButton color="primary">
                <OrderStatusIcon status={order.state} />
              </IconButton>
            )}
            <Box
              sx={{
                width: 280,
                marginLeft: 1,
              }}
            >
              {orderCardConfig && (
                <Stack
                  direction={"row"}
                  alignContent={"center"}
                  justifyContent={"space-between"}
                  spacing={1}
                  width={"100%"}
                >
                  {[orderCardConfig.topLeft, orderCardConfig.topRight].map(
                    (key) => {
                      return (
                        key && (
                          <TooltipWrapper
                            title={getValueByConfigDetail(order, key)}
                            key={`${key}`}
                          >
                            <Typography
                              variant="subtitle2"
                              noWrap={true}
                              width={"50%"}
                              textAlign={
                                key === orderCardConfig.topRight
                                  ? "right"
                                  : "left"
                              }
                              marginLeft={
                                key === orderCardConfig.topRight
                                  ? "auto !important"
                                  : 0
                              }
                            >
                              {getValueByConfigDetail(order, key)}
                            </Typography>
                          </TooltipWrapper>
                        )
                      )
                    },
                  )}
                </Stack>
              )}
              <TooltipWrapper title={order.oneLineAddress}>
                <Typography
                  data-testid={`order-address-${order.id}`}
                  maxWidth={"100%"}
                  noWrap={true}
                  variant="body2"
                  sx={(theme) => ({
                    color: theme.palette.primary.main,
                  })}
                >
                  {order.oneLineAddress}
                </Typography>
              </TooltipWrapper>
              {orderCardConfig && (
                <>
                  <Box
                    sx={{
                      marginY:
                        orderCardConfig.deliveryConstraint ===
                        DeliveryConstraintEnum.PROGRESS_BAR
                          ? 1
                          : 0.5,
                    }}
                  >
                    <OrderDeliveryProgress
                      deliveryConstraintType={
                        orderCardConfig.deliveryConstraint
                      }
                      isOnTimeDelivery={order.isOnTimeDelivery}
                      start={order.desiredArrivalTimeHour}
                      end={order.desiredArrivalTimeHourEnd}
                      calculatedTime={order.calculatedArrivalTime}
                    />
                  </Box>
                  <Stack
                    direction={"row"}
                    alignContent={"center"}
                    justifyContent={"space-between"}
                    spacing={1}
                    width={"100%"}
                  >
                    {[
                      orderCardConfig.bottomLeft,
                      orderCardConfig.bottomRight,
                    ].map(
                      (key) =>
                        key && (
                          <TooltipWrapper
                            title={getValueByConfigDetail(order, key)}
                            key={key}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              width="50%"
                              textAlign={
                                key === orderCardConfig.bottomRight
                                  ? "right"
                                  : "left"
                              }
                              marginLeft={
                                key === orderCardConfig.bottomRight
                                  ? "auto !important"
                                  : 0
                              }
                            >
                              {getValueByConfigDetail(order, key)}
                            </Typography>
                          </TooltipWrapper>
                        ),
                    )}
                  </Stack>
                </>
              )}
            </Box>
          </Stack>
          <Divider
            sx={(theme) => ({
              mx: 2,
              borderRightWidth: 1,
              borderColor: theme.palette.divider,
            })}
            flexItem
            orientation="vertical"
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <Stack
              direction={"row"}
              spacing={0.4}
              alignItems={"center"}
              marginBottom={1}
            >
              <LocationOn color="secondary" />
              <TooltipWrapper
                title={haulerDetailMap[order.tenantId]?.name || "Davis LLC"}
              >
                <Typography
                  data-testid={`order-provider-${order.id}`}
                  color="secondary"
                  maxWidth={250}
                  noWrap={true}
                  variant="subtitle2"
                  sx={() => ({
                    cursor: "pointer",
                  })}
                  onClick={() => {
                    if (orderCardLayoutConfig) {
                      return
                    }
                    panToLocation(haulerDetailMap[order.tenantId].address)
                  }}
                  onMouseEnter={() => setAnimateId("provider", order.tenantId)}
                  onMouseLeave={() => setAnimateId("provider", null)}
                >
                  {haulerDetailMap[order.tenantId]?.name ||
                    (orderCardLayoutConfig && "Davis LLC")}
                </Typography>
              </TooltipWrapper>
            </Stack>
            <Stack
              direction={"row"}
              spacing={0.5}
              sx={(theme) => ({
                "& .MuiIconButton-root": {
                  cursor: "default",
                  "&:hover": {
                    backgroundColor: theme.palette.background.default,
                  },
                },
              })}
            >
              {order.allTagList.map((e, index) => {
                return (
                  <SquareIcon
                    backgroundColor={"common.white"}
                    color={e.color as any}
                    borderColor={e.color}
                    key={`icon-` + index}
                  >
                    <TooltipWrapper
                      title={<OrderTagLabel tag={e} />}
                      placement="top"
                    >
                      <Box
                        component={"i"}
                        className={`fa fa-${e.icon}`}
                        sx={() => ({
                          fontSize: 18,
                        })}
                      ></Box>
                    </TooltipWrapper>
                  </SquareIcon>
                )
              })}
            </Stack>
          </Box>
        </Paper>
        {orderDetailType && (
          <Dialog
            slotProps={{ paper: uiPaper("UI-DSP-023") }}
            data-testid={`order-detail-dialog-${order.id}`}
            open
            onClose={() => openOrCloseOrderDetail(null)}
            maxWidth={"lg"}
            sx={{
              "& .MuiDialog-paper": {
                width: "800px",
              },
            }}
          >
            <Paper
              square
              sx={(theme) => ({
                paddingY: 2,
                paddingLeft: 2,
                paddingRight: 6,
              })}
            >
              <Typography
                data-testid={`order-detail-reference-${order.id}`}
                variant="h1"
                color="textPrimary"
              >
                {order.reference}&nbsp;{`(${t(order.state)})`}
              </Typography>
              <IconButton
                data-testid={`order-detail-close-btn-${order.id}`}
                onClick={() => openOrCloseOrderDetail(null)}
                sx={() => ({
                  position: "absolute",
                  right: 5,
                  top: 8.5,
                })}
              >
                <HighlightOff color="primary" />
              </IconButton>
            </Paper>
            <DialogContent
              dividers
              sx={() => ({
                paddingX: 1,
                paddingY: 0,
              })}
            >
              <OrderDetailViewContainer
                currentTab={orderDetailType}
                orderId={order.id}
              />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    )
  },
)

const getValueByConfigDetail = (
  order: OrderViewModel,
  key: OrderLayoutOptionsEnum,
): string => {
  switch (key) {
    case OrderLayoutOptionsObj.clientName:
      return getFullName(
        order.client?.firstName ?? "",
        order.client?.lastName ?? "",
      )
    case OrderLayoutOptionsObj.amount:
    case OrderLayoutOptionsObj.version:
    case OrderLayoutOptionsObj.reference:
    case OrderLayoutOptionsObj.id:
    case OrderLayoutOptionsObj.lastUpdated:
    case OrderLayoutOptionsObj.source:
      return String(order[key as keyof OrderViewModel])
    default:
      return ""
  }
}
